import { useState, useEffect, useCallback } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { Flash, Modal, FormField, ModalActions, Pagination } from "../components/AdminUI";
import "../styles/admin.css";

const KIND_LABEL = {
  holiday: "日祝ダイヤ",
  weekday: "平日ダイヤ",
};

const KIND_BADGE = {
  holiday: "adm-badge--red",
  weekday: "adm-badge--blue",
};

const WEEKDAY_JA = ["日", "月", "火", "水", "木", "金", "土"];

function formatDate(date) {
  if (!date) return "";
  const ymd = String(date).slice(0, 10);
  const d = new Date(ymd + "T00:00:00");
  if (Number.isNaN(d.getTime())) return ymd;
  return ymd + "（" + WEEKDAY_JA[d.getDay()] + "）";
}

export default function SpecialDateManager() {
  const [specialDates, setSpecialDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState({ message: "", type: "success" });

  // フィルター
  const [kindFilter, setKindFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 20;

  // 追加・編集モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ date: "", kind: "holiday", note: "" });
  const [saving, setSaving] = useState(false);

  // 削除確認
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── データ取得 ──
  const loadSpecialDates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/special-dates");
      setSpecialDates(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "特別日の取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpecialDates();
  }, [loadSpecialDates]);

  function showFlash(message, type = "success") {
    setFlash({ message, type });
  }

  // ── フィルタリング・ページネーション ──
  const filtered = specialDates.filter(
    (s) => kindFilter === "all" || s.kind === kindFilter
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  function handleKindFilterChange(v) {
    setKindFilter(v);
    setCurrentPage(1);
  }

  // ── モーダル操作 ──
  function openAdd() {
    setEditTarget(null);
    setForm({ date: "", kind: "holiday", note: "" });
    setModalOpen(true);
  }

  function openEdit(specialDate) {
    setEditTarget(specialDate);
    setForm({
      date: String(specialDate.date ?? "").slice(0, 10),
      kind: specialDate.kind ?? "holiday",
      note: specialDate.note ?? "",
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditTarget(null);
  }

  async function handleSave() {
    if (!form.date) {
      showFlash("日付は必須です", "error");
      return;
    }

    setSaving(true);
    const payload = {
      date: form.date,
      kind: form.kind,
      note: form.note.trim() === "" ? null : form.note.trim(),
    };

    try {
      if (editTarget) {
        const res = await axios.put("/admin/special-dates/" + editTarget.id, payload);
        setSpecialDates((prev) =>
          prev.map((s) => (s.id === editTarget.id ? { ...s, ...res.data } : s))
        );
        showFlash("更新しました");
      } else {
        const res = await axios.post("/admin/special-dates", payload);
        setSpecialDates((prev) => [res.data, ...prev]);
        showFlash("追加しました");
      }
      closeModal();
    } catch (e) {
      const errors = e.response?.data?.errors;
      if (errors) {
        showFlash(Object.values(errors).flat()[0], "error");
      } else {
        showFlash(e.response?.data?.message ?? "保存に失敗しました", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  // ── 削除 ──
  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete("/admin/special-dates/" + deleteTarget.id);
      setSpecialDates((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      showFlash("削除しました");
      setDeleteTarget(null);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "削除に失敗しました", "error");
    } finally {
      setDeleting(false);
    }
  }

  // ── Render ──
  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "特別日管理" }]} />

      <Flash
        message={flash.message}
        type={flash.type}
        onClose={() => setFlash({ ...flash, message: "" })}
      />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">特別日管理</h2>
          <span className="adm-badge adm-badge--violet">admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>
          ＋ 特別日を追加
        </button>
      </div>

      <div className="adm-filter-box">
        <p style={{ fontSize: ".8125rem", color: "var(--adm-text-muted)", margin: "0 0 .75rem" }}>
          暦どおりでないダイヤで走る日を登録します。お盆・年末年始など、平日なのに
          日祝ダイヤで走る日は「日祝ダイヤ」で登録してください。
          目撃情報の日付の色分けと、運用解析の集計がこの設定に従います。
        </p>
        <div className="adm-filter-row">
          <div className="adm-filter-field">
            <span className="adm-filter-label">ダイヤ種別</span>
            <select
              className="adm-input"
              value={kindFilter}
              onChange={(e) => handleKindFilterChange(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <option value="all">すべて</option>
              <option value="holiday">日祝ダイヤ</option>
              <option value="weekday">平日ダイヤ</option>
            </select>
          </div>
        </div>
      </div>

      <div className="adm-card">
        <div className="adm-card__header">
          特別日一覧
          <span className="adm-card__header-sub">{filtered.length} 件</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                {["ID", "日付", "ダイヤ種別", "メモ", "操作"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="adm-empty">読み込み中…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={5} className="adm-empty">該当する特別日がありません</td></tr>
              ) : (
                pageItems.map((specialDate) => (
                  <tr key={specialDate.id}>
                    <td className="adm-table__id">{specialDate.id}</td>
                    <td className="adm-table__bold">{formatDate(specialDate.date)}</td>
                    <td>
                      <span className={"adm-badge " + (KIND_BADGE[specialDate.kind] ?? "")}>
                        {KIND_LABEL[specialDate.kind] ?? specialDate.kind}
                      </span>
                    </td>
                    <td className="adm-table__mute">{specialDate.note ?? ""}</td>
                    <td>
                      <button
                        className="adm-btn adm-btn--default adm-btn--sm"
                        onClick={() => openEdit(specialDate)}
                      >
                        編集
                      </button>
                      <button
                        className="adm-btn adm-btn--danger adm-btn--sm"
                        onClick={() => setDeleteTarget(specialDate)}
                        style={{ marginLeft: ".4rem" }}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          current={safePage}
          total={totalPages}
          onChange={setCurrentPage}
          count={filtered.length}
        />
      </div>

      {/* 追加・編集モーダル */}
      <Modal
        show={modalOpen}
        title={editTarget ? "特別日を編集" : "特別日を追加"}
        onClose={closeModal}
      >
        <FormField label="日付" required>
          <input
            type="date"
            className="adm-input adm-input--full"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
        </FormField>
        <FormField label="ダイヤ種別" required>
          <select
            className="adm-input adm-input--full"
            value={form.kind}
            onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
          >
            <option value="holiday">日祝ダイヤ（暦は平日だが日祝ダイヤで走る）</option>
            <option value="weekday">平日ダイヤ（暦は土日祝だが平日ダイヤで走る）</option>
          </select>
          <p style={{ fontSize: ".75rem", color: "var(--adm-text-muted)", margin: ".4rem 0 0" }}>
            ※ 同じ日を二重に登録することはできません。平日ダイヤ指定のほうが優先されます。
          </p>
        </FormField>
        <FormField label="メモ">
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="例: お盆"
            maxLength={255}
          />
        </FormField>
        <ModalActions
          onCancel={closeModal}
          onConfirm={handleSave}
          confirmLabel={saving ? "保存中…" : "保存"}
          disabled={saving}
        />
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        show={!!deleteTarget}
        title="削除の確認"
        size="sm"
        onClose={() => setDeleteTarget(null)}
      >
        <p className="adm-modal__body">
          以下の特別日を削除しますか？削除するとその日は暦どおりのダイヤ種別に戻ります。
        </p>
        {deleteTarget && (
          <p className="adm-modal__confirm-target">
            {formatDate(deleteTarget.date)} / {KIND_LABEL[deleteTarget.kind] ?? deleteTarget.kind}
            {deleteTarget.note ? "（" + deleteTarget.note + "）" : ""}
          </p>
        )}
        <ModalActions
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          confirmLabel={deleting ? "削除中…" : "削除する"}
          confirmClass="adm-btn--danger"
          disabled={deleting}
        />
      </Modal>
    </div>
  );
}
